/**
 * Resistor color code: 4-band and 5-band.
 * Band colors and values; result in Ω with optional k/M suffix.
 */

import React, { useState, useMemo } from 'react';
import { resultClass, labelClass } from './parseEngineering';

const ts = () => new Date().toISOString();

const selectClass = 'px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none';

const DIGITS = [
  { name: 'Black', value: 0, bg: 'bg-black', border: 'border-gray-700' },
  { name: 'Brown', value: 1, bg: 'bg-amber-900', border: 'border-amber-800' },
  { name: 'Red', value: 2, bg: 'bg-red-800', border: 'border-red-700' },
  { name: 'Orange', value: 3, bg: 'bg-orange-600', border: 'border-orange-500' },
  { name: 'Yellow', value: 4, bg: 'bg-yellow-500', border: 'border-yellow-400' },
  { name: 'Green', value: 5, bg: 'bg-green-700', border: 'border-green-600' },
  { name: 'Blue', value: 6, bg: 'bg-blue-700', border: 'border-blue-600' },
  { name: 'Violet', value: 7, bg: 'bg-violet-600', border: 'border-violet-500' },
  { name: 'Gray', value: 8, bg: 'bg-gray-500', border: 'border-gray-400' },
  { name: 'White', value: 9, bg: 'bg-gray-200', border: 'border-gray-300' },
];

const MULTIPLIER = [...DIGITS].map((d, i) => ({ ...d, mult: Math.pow(10, i) }));
const TOLERANCE = [
  { name: '—', value: null },
  { name: '±1%', value: 1 },
  { name: '±2%', value: 2 },
  { name: '±0.5%', value: 0.5 },
  { name: '±0.25%', value: 0.25 },
  { name: '±0.1%', value: 0.1 },
  { name: '±5%', value: 5 },
  { name: '±10%', value: 10 },
];

const formatOhms = (r) => {
  if (r >= 1e6) return (r / 1e6).toFixed(2) + ' MΩ';
  if (r >= 1e3) return (r / 1e3).toFixed(2) + ' kΩ';
  return r.toFixed(0) + ' Ω';
};

const ResistorColorCode = () => {
  const [bands, setBands] = useState(4);
  const [b1, setB1] = useState(1);
  const [b2, setB2] = useState(0);
  const [b3, setB3] = useState(0);
  const [mult, setMult] = useState(1);
  const [tol, setTol] = useState(6);

  console.log(`[${ts()}] [ResistorColorCode] render`);

  const value = useMemo(() => {
    if (bands === 4) {
      const base = b1 * 10 + b2;
      return base * Math.pow(10, mult);
    }
    const base = b1 * 100 + b2 * 10 + b3;
    return base * Math.pow(10, mult);
  }, [bands, b1, b2, b3, mult]);

  const tolVal = TOLERANCE[tol]?.value;

  return (
    <div className="space-y-4">
      <div className="flex gap-4 items-center">
        <span className="text-gray-600 text-sm font-medium">Bands:</span>
        <label className="flex items-center gap-2 cursor-pointer text-gray-700">
          <input type="radio" checked={bands === 4} onChange={() => setBands(4)} className="rounded text-blue-600" />
          4-band
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-gray-700">
          <input type="radio" checked={bands === 5} onChange={() => setBands(5)} className="rounded text-blue-600" />
          5-band
        </label>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <BandSelect value={b1} onChange={setB1} options={DIGITS} label="1st" />
        <BandSelect value={b2} onChange={setB2} options={DIGITS} label="2nd" />
        {bands === 5 && <BandSelect value={b3} onChange={setB3} options={DIGITS} label="3rd" />}
        <MultiplierSelect value={mult} onChange={setMult} />
        <ToleranceSelect value={tol} onChange={setTol} options={TOLERANCE} />
      </div>
      <div className={resultClass}>
        <span className="text-gray-600 text-sm">Resistance: </span>
        <span className="font-semibold text-gray-900">{formatOhms(value)}</span>
        {tolVal != null && (
          <span className="text-gray-500 text-sm ml-2">({TOLERANCE[tol].name})</span>
        )}
      </div>
    </div>
  );
};

function BandSelect({ value, onChange, options, label }) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <select value={value} onChange={(e) => onChange(Number(e.target.value))} className={selectClass}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.name} ({o.value})</option>
        ))}
      </select>
    </div>
  );
}

function MultiplierSelect({ value, onChange }) {
  const options = MULTIPLIER.map((o, i) => ({
    ...o,
    label: o.value === 0 ? '×1' : o.value === 1 ? '×10' : `×10${o.value}`,
  }));
  return (
    <div>
      <label className={labelClass}>Multiplier</label>
      <select value={value} onChange={(e) => onChange(Number(e.target.value))} className={selectClass}>
        {options.map((o, i) => (
          <option key={i} value={i}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function ToleranceSelect({ value, onChange, options }) {
  return (
    <div>
      <label className={labelClass}>Tolerance</label>
      <select value={value} onChange={(e) => onChange(Number(e.target.value))} className={selectClass}>
        {options.map((o, i) => (
          <option key={i} value={i}>{o.name}</option>
        ))}
      </select>
    </div>
  );
}

export default ResistorColorCode;
